export class MathUtils {
    static readonly deg2Rad = Math.PI / 180;
    static readonly rad2Deg = 180 / Math.PI;

    /**
     * Maps a value from source range to target range
     * @param value Value to map
     * @param sourceMin Lower bound of source range
     * @param sourceMax Upper bound of source range
     * @param destinationMin Lower bound of target range
     * @param destinationMax Upper bound of target range
     * @param clamped Should the value be constrained to the range?
     * @returns Mapped value
     */
    static mapRange(value: number, sourceMin: number, sourceMax: number, destinationMin: number, destinationMax: number, clamped: boolean = false) {
        if (clamped) value = MathUtils.clamp(value, sourceMin, sourceMax);
        return ((value - sourceMin) / (sourceMax - sourceMin) * (destinationMax - destinationMin)) + destinationMin;
    }

    /**
     * Maps a value from source ranges to target ranges
     * @param value Value to map
     * @param sources List of source ranges
     * @param destinations List of target ranges
     * @param clamped Should the value be constrained to the ranges?
     * @returns Mapped value
     */
    static mapRanges(value: number, source: number[], destination: number[], clamped: boolean = false) {
        if (source.length !== destination.length) throw Error("Source range length not equal to destination range length");
        if (clamped) value = MathUtils.clamp(value, source[0], source[source.length - 1]);
        let sourceMin = source[0], sourceMax = source[0],
            destinationMin = destination[0], destinationMax = destination[0];
        for (let i = 1; i < source.length; ++i) {
            sourceMin = source[i - 1];
            sourceMax = source[i];
            destinationMin = destination[i - 1];
            destinationMax = destination[i];
            if (value <= sourceMax) return MathUtils.mapRange(value, sourceMin, sourceMax, destinationMin, destinationMax, clamped);
        }
        return MathUtils.mapRange(value, sourceMin, sourceMax, destinationMin, destinationMax, clamped);
    }

    static frac(value: number) {
        return value - Math.floor(value);
    }

    static lerp(a: number, b: number, t: number, clamp: boolean = false) {
        if (clamp) t = MathUtils.clamp(t, 0, 1);
        return a * (1 - t) + b * t;
    }

    /**
     * Clamp a value in a range
     * @param value Value to clamp
     * @param min Minimum value
     * @param max Maximum value
     * @returns Clamped value
     */
    static clamp(value: number, min: number, max: number): number {
        if (min > max) [min, max] = [max, min];
        if (value < min) return min;
        if (value > max) return max;
        return value;
    }

    /**
     * Decay a value.
     * @range [`min`,`max`]
     * @inside linear (y=x)
     * @outside decays by `factor` and limits to 1 at infinity
     * @see https://www.desmos.com/calculator/gx2p5jxvwh
     * @param value Value to decay
     * @param factor Decay factor. Default 1
     * @param min Lower bound of linear range. Default -1
     * @param max Upper bound of linear range. Default 1
     * @returns Decayed value
     */
    static decay(value: number, factor: number = 1, min: number = -1, max: number = 1) {
        const x = MathUtils.mapRange(value, min, max, -1, 1);
        const f = Math.max(0, factor);
        const sign = Math.sign(x);
        const abs = Math.abs(x);
        return sign * Math.pow(abs, 1 / Math.pow(Math.max(1, abs), f));
    }
}